/* eslint-disable react/jsx-no-useless-fragment */
import { useConfig } from '@revenuewell/logic-config';
import { useDesktop } from '@revenuewell/logic-desktop';
import { useFeatureFlag } from '@revenuewell/logic-feature-flags';
import { useProgress } from '@revenuewell/logic-progress';
import { useProxy } from '@revenuewell/logic-wrapper-proxy';
import React, { useEffect, useState } from 'react';
import { useMobileFocus } from '../../hooks/mobile-focus/mobile-focus';
import { isWebView as isMobileUserAgent } from '../../utils/mobile';
import { NewAppAvailable } from '../new-app-available/new-app-available';
import { ProductMenu } from '../product-menu/product-menu';
import { AppTemplateRootLayout } from './app-template-root-layout/app-template-root-layout';

/* eslint-disable-next-line */
export interface IAppTemplateRootProps {
  ready: boolean;
  spinnerMessage?: string;
  className?: string;
  productMenu?: {
    render: boolean;
  };
  children?: React.ReactNode;
  suppressRedirectToWrapper?: boolean;
}

export const AppTemplateRoot: React.FC<IAppTemplateRootProps> = ({
  children,
  ready,
  spinnerMessage,
  className,
  productMenu,
  suppressRedirectToWrapper
}) => {
  const productMenuReadyDelay = 100; // user friendly Product Menu render delay
  const { config } = useConfig();
  const { proxy } = useProxy();
  const { hasFlag } = useFeatureFlag();
  const { mobileFocus } = useMobileFocus();
  const { hideAppSpinner } = useProgress();
  const { isDesktopApplication, isNewDesktopApp, isDesktopWidget } = useDesktop();
  const [productMenuReady, setProductMenuReady] = useState<boolean>(false);
  const [productMenuVisible, setProductMenuVisible] = useState<boolean>(false);
  const isRedirectToWrapper =
    !suppressRedirectToWrapper &&
    !proxy &&
    !isMobileUserAgent() &&
    config.wrapper?.frame &&
    ((isDesktopApplication() && !isDesktopWidget) || hasFlag('UnifiedWrapper'));

  useEffect(() => {
    if (isRedirectToWrapper) {
      const location = window.location;
      location.href = `${config.wrapper.url}/${config.wrapper.frame}${location.pathname}${location.search}${location.hash}`;
    }
  }, []);

  useEffect(() => {
    if (!isRedirectToWrapper && ready) hideAppSpinner();
  }, [ready, isRedirectToWrapper]);

  useEffect(() => {
    if (ready && productMenuReady) setTimeout(() => setProductMenuVisible(true), productMenuReadyDelay);
  }, [ready, productMenuReady]);

  if (isDesktopApplication() && !isNewDesktopApp()) {
    return <NewAppAvailable />;
  } else {
    return (
      <>
        <AppTemplateRootLayout ready={ready} className={className}>
          {productMenu && productMenu.render && !proxy && !isMobileUserAgent() ? (
            <ProductMenu
              visible={productMenuVisible}
              focused={mobileFocus === 'drawer'}
              onReady={() => setProductMenuReady(true)}
            />
          ) : null}
          {!isRedirectToWrapper && ready ? <>{children}</> : null}
        </AppTemplateRootLayout>
      </>
    );
  }
};
